import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, ReactNode } from 'react';

import { apiService } from '../services/api';
import { resolveTenant, type TenantConfig } from '../config/tenant';
import { darken, lighten, readableForeground } from '../utils/color';
import type { BankAccountSettings, BookingWindowSettings } from '../types';

export const DEFAULT_PLATFORM_NAME = 'Easy and Quiet hostel';

export type CardGatewayAvailability = {
  flutterwave: boolean;
  paystack: boolean;
};

// Until the settings endpoint responds, assume the historical default of
// Flutterwave-only so the card option never disappears on a slow network.
const DEFAULT_CARD_GATEWAYS: CardGatewayAvailability = {
  flutterwave: true,
  paystack: false,
};

// Single source of the closed-window copy on the client, mirroring
// DEFAULT_BOOKING_CLOSED_MESSAGE in the backend's settings.service.ts. Only
// ever rendered if the settings endpoint is unreachable — a served response
// always carries the admin's own wording.
export const DEFAULT_BOOKING_CLOSED_MESSAGE =
  'Registration and accommodation booking are closed until the semester opens. Please check back when the semester opens.';

// Default to closed so a failed settings fetch cannot expose registration or
// sign-in before the semester opens. Callers must gate on `loading` before
// rendering a closed state, or every visitor sees a false "closed" flash on
// each cold load — see LoginPage/RegisterPage for the pattern.
const DEFAULT_BOOKING_WINDOW: BookingWindowSettings = {
  status: 'CLOSED',
  message: DEFAULT_BOOKING_CLOSED_MESSAGE,
  reopensAt: null,
  registrationOpen: false,
  signInOpen: false,
};

// One retry before giving up. Defaulting closed means a single transient
// blip would otherwise present a shut portal for the rest of the session.
const SETTINGS_RETRY_DELAY_MS = 1500;

interface BrandContextType {
  /** Current hostname's brand identity (name, colors, copy) — see config/tenant.ts. */
  tenant: TenantConfig;
  platformName: string;
  bankAccount: BankAccountSettings | null;
  cardGateways: CardGatewayAvailability;
  bookingWindow: BookingWindowSettings;
  loading: boolean;
  refresh: () => Promise<void>;
  setPlatformName: (name: string) => void;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

interface BrandProviderProps {
  children: ReactNode;
}

export const BrandProvider: React.FC<BrandProviderProps> = ({ children }) => {
  const tenant = useMemo(() => resolveTenant(), []);
  const [platformName, setPlatformNameState] = useState(tenant.displayName || DEFAULT_PLATFORM_NAME);
  const [bankAccount, setBankAccount] = useState<BankAccountSettings | null>(null);
  const [cardGateways, setCardGateways] = useState<CardGatewayAvailability>(DEFAULT_CARD_GATEWAYS);
  const [bookingWindow, setBookingWindow] = useState<BookingWindowSettings>(DEFAULT_BOOKING_WINDOW);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      let settings: Awaited<ReturnType<typeof apiService.getPublicSettings>>;
      try {
        settings = await apiService.getPublicSettings();
      } catch {
        await new Promise((resolve) => setTimeout(resolve, SETTINGS_RETRY_DELAY_MS));
        settings = await apiService.getPublicSettings();
      }
      // The platformName admin setting predates multi-brand support and only
      // describes the default (Easy & Quiet) brand — applying it on other
      // brand domains would overwrite their name with Easy & Quiet's.
      // TODO: replace with a per-brand setting once one exists.
      if (settings?.platformName?.trim() && tenant.brand === 'EASY_AND_QUIET') {
        setPlatformNameState(settings.platformName.trim());
      }
      if (settings?.bankAccount) {
        setBankAccount(settings.bankAccount);
      }
      if (settings?.cardGateways) {
        setCardGateways(settings.cardGateways);
      }
      if (settings?.bookingWindow) {
        setBookingWindow(settings.bookingWindow);
      }
    } catch {
      // Keep the current brand name if the settings endpoint is unreachable.
      // bookingWindow stays at the closed default, which is the safe side of
      // the trade: students see the closed notice rather than a booking flow
      // the server would refuse anyway.
    } finally {
      setLoading(false);
    }
  }, [tenant.brand]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Applies the resolved brand's colors as CSS custom property overrides.
  // index.css/tailwind.config already read --primary/--brand/--ring/--accent
  // everywhere (buttons, links, focus rings), so this is the only place a
  // brand's palette needs to be wired in — no component-level changes.
  // useLayoutEffect (not useEffect) so this runs before first paint, instead
  // of after the default teal palette has already flashed on screen.
  //
  // EASY_AND_QUIET is intentionally skipped: index.css's :root defaults ARE
  // that brand's tuned palette, hand-picked independently of tenant.ts's
  // primaryColor/accentColor (which don't byte-for-byte match — e.g. the
  // CSS's default --accent is amber, tenant.ts's EASY_AND_QUIET.accentColor
  // is teal). Overriding unconditionally would silently change Easy &
  // Quiet's live look as a side effect of fixing An Noor's.
  useLayoutEffect(() => {
    if (tenant.brand === 'EASY_AND_QUIET') return;

    const root = document.documentElement.style;
    root.setProperty('--primary', tenant.primaryColor);
    root.setProperty('--primary-foreground', readableForeground(tenant.primaryColor));
    root.setProperty('--brand', tenant.primaryColor);
    root.setProperty('--ring', tenant.primaryColor);
    root.setProperty('--brand-light', lighten(tenant.primaryColor, 0.15));
    root.setProperty('--brand-dark', darken(tenant.primaryColor, 0.1));
    root.setProperty('--accent', tenant.accentColor);
    root.setProperty('--accent-foreground', readableForeground(tenant.accentColor));
    root.setProperty('--brand-accent', tenant.accentColor);
    root.setProperty('--brand-accent-light', lighten(tenant.accentColor, 0.15));
  }, [tenant]);

  return (
    <BrandContext.Provider
      value={{
        tenant,
        platformName,
        bankAccount,
        cardGateways,
        bookingWindow,
        loading,
        refresh,
        setPlatformName: setPlatformNameState,
      }}
    >
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = (): BrandContextType => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
};
